import { ChartFrame, LineLayer, NegativeShade, ZeroLine } from '@finsim/charts';
import { useUnit } from 'effector-react';
import { useMemo } from 'react';
import type { ScenarioResult, SeriesPair } from '../../../entities/simulation';
import { cn } from '../../../shared/lib/cn';
import { explainOpened } from '../model';
import { scenarioColor } from '../lib/series';
import type { CanvasData } from './use-canvas';
import { summarize } from './use-canvas';

export interface SeriesChartProps {
  data: CanvasData;
  title: string;
  /** The series drawn for each visible variant. Keep it stable: the lines are memoized on it. */
  select: (result: ScenarioResult) => SeriesPair | undefined;
  height: number;
  hover: number;
  onHover: (i: number) => void;
  shadeNegative?: boolean;
  showEvents?: boolean;
  /** Tooltip values; money by default. */
  format?: (value: number) => string;
  /** Axis labels; compact money by default. */
  formatY?: (value: number) => string;
  yDomain?: readonly [number, number];
  /** Replaces the default summary of the lines' values. */
  ariaLabel?: string;
  /** Click or Enter on a month explains it (for the cash-flow series it can explain). */
  explain?: boolean;
}

/** One line per visible variant for a series; click a month to explain it. */
export function SeriesChart({
  data,
  title,
  select,
  height,
  hover,
  onHover,
  shadeNegative = false,
  showEvents = false,
  format,
  formatY,
  yDomain,
  ariaLabel,
  explain: explains = true,
}: SeriesChartProps) {
  const explain = useUnit(explainOpened);
  const { result, visible, valueMode, dates, range, fmt, focused } = data;
  const lines = useMemo(
    () =>
      visible.flatMap((s) => {
        const r = result.scenarios[s.id];
        const values = r && select(r)?.[valueMode];
        return values ? [{ scenario: s, values }] : [];
      }),
    [visible, result, select, valueMode],
  );
  const domainSeries = useMemo(() => lines.map((l) => l.values), [lines]);
  const focusedResult = focused ? result.scenarios[focused.id] : undefined;
  const shaded = shadeNegative && focusedResult ? select(focusedResult)?.[valueMode] : undefined;
  const value = format ?? fmt.money;

  return (
    <ChartFrame
      markers={data.markers}
      dates={dates}
      range={range}
      height={height}
      {...(yDomain ? { yDomain } : { domainSeries })}
      formatY={formatY ?? fmt.compact}
      formatXTick={fmt.year}
      ariaLabel={
        ariaLabel ??
        summarize(
          title,
          lines.map((l) => ({ label: l.scenario.name, values: l.values })),
          range,
          fmt.compact,
        )
      }
      hoverIndex={hover}
      onHoverChange={onHover}
      events={showEvents ? data.events : undefined}
      onSelectIndex={
        focused && explains
          ? (index) => {
              explain({ scenarioId: focused.id, index });
            }
          : undefined
      }
      renderTooltip={(i) => (
        <div className="space-y-1">
          <p className="text-text-3">{dates[i] ? fmt.monthLong(dates[i]) : ''}</p>
          {lines.map(({ scenario, values }) => (
            <p key={scenario.id} className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-text-2">
                <span
                  aria-hidden
                  className="h-0.5 w-2.5 rounded-full"
                  style={{ background: scenarioColor(scenario) }}
                />
                {scenario.name}
              </span>
              <span className={cn('tabular font-medium', (values[i] ?? 0) < 0 && 'text-negative')}>
                {value(values[i] ?? 0)}
              </span>
            </p>
          ))}
        </div>
      )}
    >
      {shaded && <NegativeShade values={shaded} />}
      <ZeroLine />
      {lines.map(({ scenario, values }) => (
        <LineLayer key={scenario.id} values={values} color={scenarioColor(scenario)} />
      ))}
    </ChartFrame>
  );
}
